import { useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Loader2, ShieldAlert, Info, CheckCircle2, AlertTriangle, XCircle, X } from "lucide-react";

/* ------------------------------- Button --------------------------------- */
const BTN_BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
const BTN_VARIANTS = {
  primary: "bg-primary text-primary-foreground shadow-sm hover:bg-[#B80F31] active:bg-[#a00d2b]",
  secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-slate-800",
  outline: "border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50",
  ghost: "text-slate-700 hover:bg-slate-100",
  destructive: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
};
const BTN_SIZES = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-6 text-base",
  icon: "h-9 w-9 p-0",
};
export const Button = ({ variant = "primary", size = "md", className, ...props }) => (
  <button className={cn(BTN_BASE, BTN_VARIANTS[variant], BTN_SIZES[size], className)} {...props} />
);

/* -------------------------------- Card ---------------------------------- */
export const Card = ({ className, highlighted = false, ...props }) => (
  <div
    className={cn(
      "rounded-lg bg-white shadow-sm",
      highlighted
        ? "border-l-4 border-l-primary border-y border-r border-slate-200"
        : "border border-slate-200",
      className
    )}
    {...props}
  />
);
export const CardHeader = ({ title, icon: Icon, action, className }) => (
  <div className={cn("flex items-center justify-between gap-3 px-5 pt-5", className)}>
    <div className="flex items-center gap-2.5 min-w-0">
      {Icon && (
        <span className="grid h-8 w-8 place-items-center rounded-md bg-accent text-primary shrink-0">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <h3 className="text-base font-semibold text-slate-900 truncate">{title}</h3>
    </div>
    {action}
  </div>
);
export const CardBody = ({ className, ...props }) => (
  <div className={cn("p-5", className)} {...props} />
);

/* ------------------------------- Badges --------------------------------- */
const BADGE_VARIANTS = {
  neutral: "bg-slate-100 text-slate-700 border border-slate-200",
  active: "bg-accent text-accent-foreground border border-red-200",
  implemented: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  vision: "bg-amber-50 text-amber-700 border border-amber-200",
  info: "bg-blue-50 text-blue-700 border border-blue-200",
  mono: "bg-slate-100 text-slate-700 border border-slate-200 font-mono",
};
export const Badge = ({ variant = "neutral", className, ...props }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
      BADGE_VARIANTS[variant],
      className
    )}
    {...props}
  />
);

/* ------------------------------- Alerts --------------------------------- */
const ALERT_CFG = {
  info: { cls: "bg-blue-50 border-blue-200 text-blue-900", icon: Info, iconCls: "text-blue-600" },
  success: { cls: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: CheckCircle2, iconCls: "text-emerald-600" },
  warning: { cls: "bg-amber-50 border-amber-200 text-amber-900", icon: AlertTriangle, iconCls: "text-amber-600" },
  error: { cls: "bg-red-50 border-red-200 text-red-900", icon: XCircle, iconCls: "text-red-600" },
};
export const Alert = ({ variant = "info", title, children, className, ...props }) => {
  const cfg = ALERT_CFG[variant];
  const Icon = cfg.icon;
  return (
    <div className={cn("flex gap-3 rounded-md border p-4 text-sm", cfg.cls, className)} role="alert" {...props}>
      <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", cfg.iconCls)} />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "text-sm/relaxed")}>{children}</div>}
      </div>
    </div>
  );
};

/* --------------------------- Common states ------------------------------ */
export const Loading = ({ label = "Loading…", className }) => (
  <div className={cn("flex flex-col items-center justify-center gap-3 p-12 text-slate-500", className)} data-testid="loading-state">
    <Loader2 className="h-7 w-7 animate-spin text-primary" />
    <p className="text-sm">{label}</p>
  </div>
);

export const EmptyState = ({ icon: Icon, title, description, action, className, testid = "empty-state" }) => (
  <div className={cn("rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center", className)} data-testid={testid}>
    {Icon && (
      <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-slate-100 text-slate-500">
        <Icon className="h-5 w-5" />
      </span>
    )}
    {title && <p className="text-base font-semibold text-slate-900">{title}</p>}
    {description && <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);

export const AccessDenied = ({ message, className }) => (
  <div
    className={cn("mx-auto my-8 max-w-md rounded-lg border border-red-200 bg-red-50 p-8 text-center", className)}
    role="alert"
    data-testid="authorization-denied"
  >
    <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-red-100 text-primary">
      <ShieldAlert className="h-6 w-6" />
    </span>
    <p className="text-base font-semibold text-slate-900">Access Restricted</p>
    <p className="mx-auto mt-1 max-w-xs text-sm text-slate-600">
      {message || "Your current role doesn't include permission for this area."}
    </p>
  </div>
);

/* ------------------------------ Typography ------------------------------ */
export const SectionLabel = ({ className, ...props }) => (
  <p className={cn("text-xs font-semibold uppercase tracking-wider text-slate-500", className)} {...props} />
);

export const PageHeader = ({ title, subtitle, action, className }) => (
  <div className={cn("flex flex-wrap items-end justify-between gap-3", className)}>
    <div className="min-w-0">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-slate-500">{subtitle}</p>}
    </div>
    {action}
  </div>
);

/* -------------------------------- Inputs -------------------------------- */
export const TextInput = ({ className, invalid = false, ...props }) => (
  <input
    className={cn(
      "w-full rounded-md border bg-white px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary",
      invalid ? "border-red-400" : "border-slate-300",
      className
    )}
    {...props}
  />
);

export const Field = ({ label, htmlFor, error, hint, children, className }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && (
      <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wide text-slate-700">
        {label}
      </label>
    )}
    {children}
    {error ? (
      <p className="text-xs font-medium text-red-600">{error}</p>
    ) : hint ? (
      <p className="text-xs text-slate-500">{hint}</p>
    ) : null}
  </div>
);

/* -------------------------------- Select -------------------------------- */
export const Select = ({ className, invalid = false, children, ...props }) => (
  <select
    className={cn(
      "w-full rounded-md border bg-white px-3 py-2 text-base text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary",
      invalid ? "border-red-400" : "border-slate-300",
      className
    )}
    {...props}
  >
    {children}
  </select>
);

/* ------------------------------- Textarea ------------------------------- */
export const Textarea = ({ className, invalid = false, ...props }) => (
  <textarea
    className={cn(
      "w-full rounded-md border bg-white px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary",
      invalid ? "border-red-400" : "border-slate-300",
      className
    )}
    {...props}
  />
);

/* -------------------------------- Tabs ---------------------------------- */
export const Tabs = ({ tabs, value, onChange, className }) => (
  <div className={cn("flex flex-wrap gap-1 border-b border-slate-200", className)} role="tablist">
    {tabs.map((t) => {
      const active = value === t.key;
      return (
        <button
          key={t.key}
          role="tab"
          aria-selected={active}
          data-testid={`tab-${t.key}`}
          onClick={() => onChange(t.key)}
          className={cn(
            "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
            active
              ? "border-primary text-primary"
              : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
          )}
        >
          {t.label}
          {typeof t.count === "number" && (
            <span className={cn("ml-2 rounded-full px-1.5 py-0.5 text-xs", active ? "bg-accent text-primary" : "bg-slate-100 text-slate-500")}>{t.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

/* -------------------------------- Modal --------------------------------- */
export const Modal = ({ open, onClose, title, description, children, footer, testid = "modal", size = "md" }) => {
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  const width = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" }[size] || "max-w-lg";
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6" data-testid={testid}>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn("relative z-10 mt-8 w-full rounded-xl border border-slate-200 bg-white shadow-2xl", width)}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
          </div>
          <button
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3.5">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

/* ---------------------------- ConfirmDialog ----------------------------- */
export const ConfirmDialog = ({ open, onCancel, onConfirm, title, children, confirmLabel = "Confirm", busy = false, destructive = true, testid = "confirm-dialog" }) => (
  <Modal
    open={open}
    onClose={onCancel}
    title={title}
    testid={testid}
    size="sm"
    footer={
      <>
        <Button variant="outline" size="sm" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Button>
        <Button
          variant={destructive ? "destructive" : "primary"}
          size="sm"
          onClick={onConfirm}
          disabled={busy}
          data-testid={`${testid}-confirm`}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </>
    }
  >
    <div className="text-sm text-slate-600">{children}</div>
  </Modal>
);
